import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import { Breadcrumb, Spin } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

interface PICard {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: { pid_product_id: number; pid_qty: number }[];
}

const getCardBorderColor = (pi_date: string, lead_time: number): string => {
  const deadline = dayjs(pi_date).add(lead_time, 'day');
  const today = dayjs();
  const daysLeft = deadline.diff(today, 'day');

  if (daysLeft < 0) return '#ff4d4f';       // 🔴 Red - PO delay
  if (daysLeft <= 3) return '#faad14';       // 🟡 Yellow - Work order delay
  return '#52c41a';                          // 🟢 Green - On time
};

const getCardStatus = (pi_date: string, lead_time: number): string => {
  const deadline = dayjs(pi_date).add(lead_time, 'day');
  const today = dayjs();
  const daysLeft = deadline.diff(today, 'day');

  if (daysLeft < 0) return `${Math.abs(daysLeft)} days overdue`;
  if (daysLeft === 0) return 'Due today';
  return `${daysLeft} days left`;
};

const DailyProgress: React.FC = () => {
  const [invoices, setInvoices] = useState<PICard[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const [piData, woData] = await Promise.all([
        apiRequest<PICard[]>({
          method: 'get',
          url: '/get-performa-invoices',
        }),
        apiRequest<any[]>({
          method: 'get',
          url: '/get-work-orders',
        }),
      ]);
      setInvoices(piData || []);
      setWorkOrders(woData || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
        <Breadcrumb.Item>Workflow</Breadcrumb.Item>
      </Breadcrumb>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          {invoices.map((inv) => {
            const borderColor = getCardBorderColor(inv.pi_date, inv.pi_lead_time);

            const pWorkOrders = workOrders.filter((wo) => wo.wo_pi_id === inv.pi_id);
            const totalWO = pWorkOrders.length;
            const completedWO = pWorkOrders.filter(
              (wo) => wo.wo_status === 'completed' || Number(wo.total_complete || 0) >= Number(wo.wo_qty)
            ).length;

            return (
              <div
                key={inv.pi_id}
                onClick={() => navigate(`/workflow/${inv.pi_id}`)}
                style={{
                  width: 220,
                  border: `3px solid ${borderColor}`,
                  borderRadius: 12,
                  padding: '16px 18px',
                  cursor: 'pointer',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 16px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                {/* Color dot */}
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: borderColor,
                  }}
                />

                {/* PI Number */}
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>
                  PI: #{inv.pi_order_no}
                </div>

                {/* PI ID */}
                <div style={{ fontSize: 12, color: '#999', marginBottom: 10 }}>
                  PI-{inv.pi_id}
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1px solid #f0f0f0', marginBottom: 10 }} />

                {/* Total WO and Completed WO */}
                <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
                  📝 Total WO: {totalWO}
                </div>

                <div style={{ fontSize: 13, color: '#555', marginBottom: 10 }}>
                  ✅ Completed WO: {completedWO}
                </div>
              </div>
            );
          })}

          {invoices.length === 0 && (
            <div style={{ color: '#aaa', fontSize: 15 }}>No PI found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyProgress;